import { APP_URL_PARAMS, appUrl } from '@versesverse/schema/app-url';
import type { AppUrlState } from '@versesverse/schema/app-url';

/*
 * THE ADDRESS BAR AS THE SHARE BUTTON.
 *
 * Every view worth sending someone — a figure, a passage, two accounts side by side, a panel, a
 * discussion — is a bit of React state that, until it reached the URL, no one could copy, bookmark
 * or paste. Reflecting that state into the address bar makes the browser's own copy/share/bookmark
 * the share feature, so no "copy link" control has to exist on every surface to duplicate what the
 * browser already does better. Arriving with such a URL restores the view.
 *
 * Everything is addressed by a STABLE identifier, never a build-specific one: a node by its id, a
 * passage by its citation, a comparison by the ids the build derives from the text (a story id, a
 * pair of ranges). Verse ids are re-numbered on every rebuild, so a link that carried one would rot
 * the next time the library is published; a citation does not.
 *
 * replaceState, not pushState. The app keeps its own selection history and its own Back control, and
 * pushing an entry per view would hand the browser's back button a stack the app already manages —
 * one press would step through views the reader never asked to revisit. The URL FOLLOWS the state
 * here; it does not drive it. Parameters the app does not own (e.g. `?worker=0`, which turns the map's
 * render worker off for debugging) are preserved, so opening a view never strips a debugging flag.
 */

/*
 * THE VOCABULARY ITSELF IS IN `schema/app-url.ts`, and the reason is the 36 episode pages.
 *
 * This module is about the WINDOW — reading the address bar, writing it back without stepping on
 * parameters the app does not own, and doing it once per real change. The names and their meanings
 * are a contract with the other codebase: `pipeline/` writes 52,406 of these URLs into the static
 * pages, and it was spelling them by hand. That is how every `?compare=` link in the library came
 * to open the front door instead of the comparison, undetected for the life of the feature, while a
 * gate that built its own copy of the format passed every run.
 *
 * So the declaration lives where both halves can reach it, and neither writes the format down.
 */
export type { AppUrlState } from '@versesverse/schema/app-url';

/** The query parameters the app owns, in the order they are written — every other param is preserved. */
const PARAMS = APP_URL_PARAMS;

/**
 * The view a URL is asking for. Each value is trimmed; a blank one (`?node=`, what a truncated or
 * hand-edited link looks like) is dropped rather than restored, so it lands on the map, not on a
 * lookup for "".
 */
export function readAppUrl(search: string): AppUrlState {
  const params = new URLSearchParams(search);
  const state: AppUrlState = {};
  for (const key of PARAMS) {
    const raw = params.get(key);
    if (raw === null) continue;
    const trimmed = raw.trim();
    if (trimmed !== '') state[key] = trimmed;
  }
  return state;
}

/**
 * The same search string with the app's parameters set from `state` (and cleared where a field is
 * absent). Returns a string beginning with `?`, or empty when nothing is left — so closing the last
 * view returns to a clean URL rather than a bare `?`.
 *
 * SPELLED BY `appUrl`, NOT BY `URLSearchParams`, and the difference is visible in the address bar.
 * `URLSearchParams.toString()` writes a space as `+`; `schema/app-url.ts` uses `encodeURIComponent`
 * and writes `%20`, deliberately — its own note records that switching would have silently rewritten
 * all 52,406 published links. This module wrote the other one, so a reader arriving on a link the
 * site publishes had it REWRITTEN under them:
 *
 *     sent        ?passage=Genesis%201%3A1
 *     address bar ?passage=Genesis+1%3A1
 *
 * Both parse to the same view, so nothing was broken — and a reader who shared what was in front of
 * them sent a different string from the one they were given, and the worker held two cache entries
 * for one page. Two spellers for one format is the fault this file's own header is about.
 *
 * PARAMETERS THE APP DOES NOT OWN KEEP THEIR RAW TEXT — `?worker=0`, `?v8=1`, `?mock=1`. They are
 * not re-encoded, because rewriting somebody else's parameter is the same mistake one level down.
 */
export function writeAppUrl(search: string, state: AppUrlState): string {
  const owned = new Set<string>(PARAMS);
  const others = search.replace('?', '').split('&')
    .filter(part => part !== '' && !owned.has(part.split('=')[0]));
  /* `appUrl` with no origin gives `/?a=b` or `/`, so what follows the `/?` is what it would publish. */
  const published = appUrl('', state);
  const ours = published.length > 1 ? published.slice(2) : '';
  const parts = [...(ours === '' ? [] : [ours]), ...others];
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

/** Everything a permalink needs from the window, so the caller can be tested without one. */
export interface UrlHost {
  readonly search: string;
  readonly pathname: string;
  replace: (url: string) => void;
}

/**
 * Reflect the whole view into the address bar. Called on every change; it compares before writing
 * because React re-renders freely and a replaceState per render is both wasteful and, in some
 * browsers, rate-limited — the guard keeps this to one write per actual change.
 */
export function syncAppUrl(host: UrlHost, state: AppUrlState): void {
  const next = writeAppUrl(host.search, state);
  if (next === host.search) return;
  host.replace(`${host.pathname}${next}`);
}

/** The live window, adapted. Absent during server rendering and in a bare test environment. */
export function browserUrlHost(): UrlHost | null {
  if (typeof window === 'undefined' || !window.history?.replaceState) return null;
  return {
    get search() { return window.location.search; },
    get pathname() { return window.location.pathname; },
    replace: (url: string) => window.history.replaceState(null, '', url),
  };
}
